import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import {
  approveShopInquiry,
  getShopInquiries,
  rejectShopInquiry,
} from '../utils/api';
import { getShopMinimumAdvance } from '../utils/shopOrderType';
import { roundRupees } from '../utils/money';

const STATUS_COLORS = {
  pending: 'bg-amber-100 text-amber-800',
  approved: 'bg-emerald-100 text-emerald-800',
  rejected: 'bg-red-100 text-red-800',
};

function formatDate(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatMoney(amount) {
  return `₹${roundRupees(amount || 0).toLocaleString('en-IN')}`;
}

const ShopInquiriesPanel = ({ onCountChange }) => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [expandedId, setExpandedId] = useState(null);
  const [approveTarget, setApproveTarget] = useState(null);
  const [approveAdvance, setApproveAdvance] = useState('');
  const [approveAgreedPrice, setApproveAgreedPrice] = useState('');
  const [approveNote, setApproveNote] = useState('');
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(false);

  const loadInquiries = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getShopInquiries({
        status: statusFilter,
        search: search.trim(),
        page: pagination.page,
        limit: 20,
      });
      setInquiries(data.inquiries || []);
      setPagination((prev) => ({
        ...prev,
        total: data.total || 0,
        totalPages: data.totalPages || 1,
      }));
    } catch (error) {
      toast.error(error.message || 'Failed to load shop inquiries');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search, pagination.page]);

  useEffect(() => {
    loadInquiries();
  }, [loadInquiries]);

  const originalPrice = approveTarget ? roundRupees(approveTarget.grandTotal) : 0;
  const hasAgreed =
    approveAgreedPrice !== '' &&
    approveAgreedPrice !== null &&
    Number.isFinite(Number(approveAgreedPrice));
  const effectiveTotal = hasAgreed ? roundRupees(Number(approveAgreedPrice)) : originalPrice;
  const minWholesaleAdvance = useMemo(
    () =>
      approveTarget?.orderType === 'wholesale'
        ? getShopMinimumAdvance(effectiveTotal)
        : 0,
    [approveTarget, effectiveTotal]
  );

  const openApprove = (inquiry) => {
    const catalogTotal = roundRupees(inquiry.grandTotal);
    const minAdvance =
      inquiry.orderType === 'wholesale' ? getShopMinimumAdvance(catalogTotal) : 0;
    const defaultAdvance = Math.max(inquiry.totalAdvance || 0, minAdvance);
    setApproveTarget(inquiry);
    setApproveAgreedPrice('');
    setApproveAdvance(String(defaultAdvance));
    setApproveNote('');
  };

  const handleAgreedChange = (raw) => {
    if (raw === '') {
      setApproveAgreedPrice('');
      const catalogTotal = approveTarget ? roundRupees(approveTarget.grandTotal) : 0;
      setApproveAdvance((prev) => {
        const current = roundRupees(parseFloat(prev) || 0);
        return String(Math.min(current, catalogTotal));
      });
      return;
    }
    const next = Number.isNaN(parseFloat(raw)) ? 0 : parseFloat(raw);
    setApproveAgreedPrice(next);
    const agreed = roundRupees(Math.max(0, next));
    setApproveAdvance((prev) => {
      const current = roundRupees(parseFloat(prev) || 0);
      return String(Math.min(current, agreed));
    });
  };

  const handleApprove = async () => {
    if (!approveTarget) return;
    const totalAdvance = roundRupees(parseFloat(approveAdvance) || 0);
    if (hasAgreed && Number(approveAgreedPrice) < 0) {
      toast.error('Agreed price cannot be negative');
      return;
    }
    if (totalAdvance < 0) {
      toast.error('Advance cannot be negative');
      return;
    }
    if (totalAdvance > effectiveTotal) {
      toast.error('Advance cannot exceed order total');
      return;
    }
    if (approveTarget.orderType === 'wholesale') {
      if (totalAdvance < minWholesaleAdvance) {
        toast.error(
          `Wholesale requires at least 40% advance (₹${minWholesaleAdvance.toLocaleString('en-IN')})`
        );
        return;
      }
    }

    setProcessing(true);
    try {
      const payload = {
        totalAdvance,
        adminNote: approveNote.trim(),
      };
      if (hasAgreed) {
        payload.settledPrice = roundRupees(Number(approveAgreedPrice));
      }
      const result = await approveShopInquiry(approveTarget._id, payload);
      toast.success(result.message || 'Inquiry approved — order created');
      setApproveTarget(null);
      setApproveAgreedPrice('');
      await loadInquiries();
      onCountChange?.();
    } catch (error) {
      toast.error(error.message || 'Failed to approve inquiry');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!rejectTarget) return;
    setProcessing(true);
    try {
      await rejectShopInquiry(rejectTarget._id, { reason: rejectReason.trim() });
      toast.success('Inquiry rejected');
      setRejectTarget(null);
      setRejectReason('');
      await loadInquiries();
      onCountChange?.();
    } catch (error) {
      toast.error(error.message || 'Failed to reject inquiry');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg shadow-md p-4 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div className="flex flex-wrap gap-2">
          {['pending', 'approved', 'rejected', ''].map((status) => (
            <button
              key={status || 'all'}
              type="button"
              onClick={() => {
                setStatusFilter(status);
                setPagination((prev) => ({ ...prev, page: 1 }));
              }}
              className={`min-h-10 px-3 rounded-lg text-sm font-medium transition-colors ${
                statusFilter === status
                  ? 'bg-amber-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {status === '' ? 'All' : status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>
        <input
          type="search"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPagination((prev) => ({ ...prev, page: 1 }));
          }}
          placeholder="Search inquiry no, name, phone…"
          className="toolbar-input max-w-full sm:max-w-xs"
        />
      </div>

      {loading ? (
        <div className="bg-white rounded-lg shadow-md p-8 text-center text-gray-600">
          Loading inquiries…
        </div>
      ) : inquiries.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-8 text-center text-gray-600">
          No shop inquiries found
        </div>
      ) : (
        <div className="space-y-3">
          {inquiries.map((inquiry) => {
            const expanded = expandedId === inquiry._id;
            return (
              <div key={inquiry._id} className="bg-white rounded-lg shadow-md overflow-hidden">
                <button
                  type="button"
                  onClick={() => setExpandedId(expanded ? null : inquiry._id)}
                  className="w-full text-left px-4 py-3 flex flex-wrap items-center gap-2 sm:gap-4 hover:bg-gray-50"
                >
                  <span className="font-semibold text-gray-900">{inquiry.inquiryNo}</span>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full capitalize ${STATUS_COLORS[inquiry.status] || 'bg-gray-100 text-gray-700'}`}>
                    {inquiry.status}
                  </span>
                  <span className="text-sm text-gray-600 capitalize">{inquiry.orderType}</span>
                  <span className="text-sm text-gray-800 font-medium">{inquiry.customerName}</span>
                  <span className="text-sm text-gray-500">{inquiry.phone}</span>
                  <span className="text-sm text-gray-700 ml-auto">{formatMoney(inquiry.grandTotal)}</span>
                  <span className="text-xs text-gray-400 w-full sm:w-auto">{formatDate(inquiry.createdAt)}</span>
                </button>

                {expanded && (
                  <div className="border-t border-gray-100 px-4 py-4 bg-gray-50/50">
                    <div className="space-y-2 mb-4">
                      {inquiry.items?.map((item, index) => (
                        <div key={index} className="flex flex-wrap gap-x-3 gap-y-1 text-sm text-gray-700">
                          <span className="font-medium">{item.modelName || 'Item'}</span>
                          <span className="text-gray-500">{item.modelNumber}</span>
                          <span>× {item.qty}</span>
                          <span>{formatMoney(item.totalValue)}</span>
                          {item.colorRequest ? (
                            <span className="text-gray-500 w-full">Note: {item.colorRequest}</span>
                          ) : null}
                        </div>
                      ))}
                    </div>

                    {inquiry.status === 'approved' && inquiry.orderNo ? (
                      <p className="text-sm text-emerald-700 mb-3">
                        Order created: <strong>{inquiry.orderNo}</strong>
                        {inquiry.totalAdvance != null ? ` · Advance ${formatMoney(inquiry.totalAdvance)}` : ''}
                      </p>
                    ) : null}

                    {inquiry.status === 'rejected' && inquiry.rejectedReason ? (
                      <p className="text-sm text-red-700 mb-3">Reason: {inquiry.rejectedReason}</p>
                    ) : null}

                    {inquiry.status === 'pending' ? (
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => openApprove(inquiry)}
                          className="px-4 py-2 bg-emerald-600 text-white text-sm font-semibold rounded-lg hover:bg-emerald-700"
                        >
                          Approve & create order
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setRejectTarget(inquiry);
                            setRejectReason('');
                          }}
                          className="px-4 py-2 bg-white border border-red-200 text-red-700 text-sm font-semibold rounded-lg hover:bg-red-50"
                        >
                          Reject
                        </button>
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {pagination.totalPages > 1 ? (
        <div className="flex justify-center gap-2">
          <button
            type="button"
            disabled={pagination.page <= 1}
            onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
            className="min-h-10 px-3 rounded-md bg-gray-200 text-gray-700 disabled:opacity-50"
          >
            Previous
          </button>
          <span className="flex items-center text-sm text-gray-600 px-2">
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <button
            type="button"
            disabled={pagination.page >= pagination.totalPages}
            onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
            className="min-h-10 px-3 rounded-md bg-gray-200 text-gray-700 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      ) : null}

      {approveTarget ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">
              Approve {approveTarget.inquiryNo}
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Enter agreed price (optional) and UPI advance collected.
              {approveTarget.orderType === 'wholesale' ? (
                <span className="block mt-1 text-amber-700">
                  Wholesale — minimum 40%: {formatMoney(minWholesaleAdvance)}
                </span>
              ) : null}
            </p>

            <div className="mb-3 text-sm">
              <span className="text-gray-500">Original price </span>
              <span className="font-semibold text-gray-900">{formatMoney(originalPrice)}</span>
              {hasAgreed ? (
                <>
                  <span className="text-gray-300 mx-1.5">·</span>
                  <span className="text-gray-500">Billed </span>
                  <span className="font-semibold text-gray-900">{formatMoney(effectiveTotal)}</span>
                </>
              ) : null}
            </div>

            <label className="block text-sm font-medium text-gray-700 mb-1">
              Agreed price (₹) <span className="font-normal text-gray-400">optional</span>
            </label>
            <input
              type="number"
              min="0"
              step="1"
              placeholder="Leave empty for original price"
              value={approveAgreedPrice === '' || approveAgreedPrice === null ? '' : approveAgreedPrice}
              onChange={(e) => handleAgreedChange(e.target.value)}
              className="toolbar-input w-full mb-3"
            />

            <label className="block text-sm font-medium text-gray-700 mb-1">Total advance (₹)</label>
            <input
              type="number"
              min="0"
              max={effectiveTotal}
              value={approveAdvance}
              onChange={(e) => setApproveAdvance(e.target.value)}
              className="toolbar-input w-full mb-3"
            />
            <p className="text-xs text-gray-500 -mt-2 mb-3">
              Max {formatMoney(effectiveTotal)}
              {approveTarget.orderType === 'wholesale'
                ? ` · Min ${formatMoney(minWholesaleAdvance)}`
                : ''}
            </p>

            <label className="block text-sm font-medium text-gray-700 mb-1">Admin note (optional)</label>
            <textarea
              value={approveNote}
              onChange={(e) => setApproveNote(e.target.value)}
              rows={2}
              className="toolbar-input w-full mb-4"
            />
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => {
                  setApproveTarget(null);
                  setApproveAgreedPrice('');
                }}
                disabled={processing}
                className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleApprove}
                disabled={processing}
                className="px-4 py-2 text-sm font-semibold text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50"
              >
                {processing ? 'Creating order…' : 'Confirm & create order'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {rejectTarget ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">
              Reject {rejectTarget.inquiryNo}
            </h3>
            <label className="block text-sm font-medium text-gray-700 mb-1 mt-3">Reason (optional)</label>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              className="toolbar-input w-full mb-4"
            />
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setRejectTarget(null)}
                disabled={processing}
                className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleReject}
                disabled={processing}
                className="px-4 py-2 text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {processing ? 'Rejecting…' : 'Reject inquiry'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ShopInquiriesPanel;
